import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';

export interface MonthlyUsageStats {
  userId: number;
  yearMonth: string;
  messageCount: number;
  limit: number;
  remaining: number;
}

interface UsageResponse {
  success: boolean;
  data?: MonthlyUsageStats;
  error?: string;
}

/**
 * Fetches the current month's usage statistics for the authenticated user.
 * Returns message count, limit, and remaining interactions.
 * Pass `{ enabled: false }` to skip the request when the caller isn't rendering usage.
 */
export function useMonthlyUsage(options?: { enabled?: boolean }) {
  const { isAuthenticated } = useAuth();
  const enabled = (options?.enabled ?? true) && isAuthenticated;

  const { data, isLoading, error, refetch } = useQuery<UsageResponse>({
    queryKey: ['/api/usage/current-month'],
    queryFn: async () => {
      const response = await fetch('/api/usage/current-month', {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch usage data');
      }
      return response.json();
    },
    enabled,
    staleTime: 30 * 1000, // 30 seconds - refresh frequently enough to show updates
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true, // Refresh when user returns to window
    retry: 1,
  });

  const stats = data?.success ? data.data : null;

  return {
    messageCount: stats?.messageCount ?? 0,
    limit: stats?.limit ?? 100000,
    remaining: stats?.remaining ?? 100000,
    yearMonth: stats?.yearMonth ?? '',
    isLoading,
    error: error || (data?.success === false ? new Error(data.error || 'Unknown error') : null),
    refetch,
  };
}
